// Live's two script tools (G8-script-tools, issue 224): "Generate Script" starts `text.script`
// from a brief, the smiley starts `text.enhance` on the script; either job's text output lands in
// the script. Live keeps one "processing" state for both, which the script field shows.
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { fetchSignedFile } from "../../api/download";
import { useGetJob, useStartJob } from "../../api/generated/endpoints/generation/generation";
import type { Job, JobRequest, ScriptTool } from "../../api/generated/model";
import { useIdempotencyKey } from "../idempotency";
import { canChange, jobRefetchInterval } from "../jobs/refetch";
import { useStreamStatus } from "../stream-status";

/** Live's shortest brief (`script-generation/schema.ts`), trimmed. */
export const MIN_BRIEF = 10;

export interface ScriptJobsOptions {
  teamId: string;
  brandId: string;
  /** Which tool asks (live's `type`): kept with the job, never sent to the model. */
  tool: ScriptTool;
  /** A script or an enhancement written: the text replaces the script, as live's does. */
  onScript: (text: string) => void;
}

type Running = { jobId: string; kind: "script" | "enhance" } | null;

/** A start's mutation, with its own Idempotency-Key rule. */
function useScriptStart(teamId: string, brandId: string, onStarted: (job: Job) => void) {
  const idempotency = useIdempotencyKey();
  const start = useStartJob({ mutation: { onSuccess: onStarted } });
  return {
    start,
    send: (data: JobRequest, then?: () => void) => {
      const key = idempotency.keyFor(data);
      start.mutate(
        { teamId, brandId, data, headers: { "Idempotency-Key": key } },
        {
          onSuccess: () => then?.(),
          onError: (error) => idempotency.failed(key, data, error),
        },
      );
    },
  };
}

/**
 * The script tools' jobs for one script field. Each start replaces the job followed; the job is
 * read as a job's page reads one (stream events, then the fallback timer), and once it succeeds
 * its text output is read from the media CDN and handed to `onScript`, once.
 */
export function useScriptJobs({ teamId, brandId, tool, onScript }: ScriptJobsOptions) {
  const stream = useStreamStatus();
  const [running, setRunning] = useState<Running>(null);
  const script = useScriptStart(teamId, brandId, (job) =>
    setRunning({ jobId: job.id, kind: "script" }),
  );
  const enhancement = useScriptStart(teamId, brandId, (job) =>
    setRunning({ jobId: job.id, kind: "enhance" }),
  );
  const job = useGetJob(teamId, running?.jobId ?? "", {
    query: {
      enabled: running !== null,
      refetchInterval: (query) => jobRefetchInterval(query.state.data?.status, stream),
    },
  });
  const status = running === null ? undefined : job.data?.status;
  const url = status === "succeeded" ? (job.data?.outputs[0]?.url ?? null) : null;
  // The job's output is one `text/plain` file (G8-script-tools).
  const text = useQuery({
    queryKey: ["script-text", running?.jobId],
    enabled: url !== null,
    queryFn: async ({ signal }) => (await fetchSignedFile(url!, signal)).text(),
    staleTime: Infinity,
    retry: false,
  });
  // The text lands once per job, through the latest callback.
  const deliver = useRef(onScript);
  useEffect(() => {
    deliver.current = onScript;
  });
  const delivered = useRef<string | null>(null);
  const jobId = running?.jobId ?? null;
  const written = text.data;
  useEffect(() => {
    if (jobId === null || written === undefined || delivered.current === jobId) return;
    delivered.current = jobId;
    deliver.current(written);
  }, [jobId, written]);
  const ended = status !== undefined && !canChange(status);
  const failed =
    running !== null && ended && (status !== "succeeded" || url === null || text.isError);
  const processing = running !== null && !failed && written === undefined;
  return {
    /** Live's "Generate Script": the trimmed brief, and the product when one is chosen. */
    generate: (brief: string, productId: string | null, then?: () => void) =>
      script.send(
        {
          type: "text.script",
          prompt: brief.trim(),
          tool,
          ...(productId === null ? {} : { product_id: productId }),
        },
        then,
      ),
    generating: script.start.isPending,
    generateError: script.start.isError ? script.start.error : null,
    /** Live's smiley: the trimmed script, with its audio tags added. */
    enhance: (current: string) =>
      enhancement.send({ type: "text.enhance", script: current.trim(), tool }),
    enhancing: enhancement.start.isPending,
    enhanceError: enhancement.start.isError ? enhancement.start.error : null,
    /** A script or an enhancement is being written: the field waits under a loader. */
    processing,
    /** The last script asked for failed (live marks it; a failed enhancement shows nothing). */
    scriptFailed: failed && running?.kind === "script",
  };
}

export type ScriptJobs = ReturnType<typeof useScriptJobs>;
